import { parseStringWithLinksIntoHtmlLinks } from '@helpers/parseStringWithLinksIntoHtmlLinks';
import { combineUserName } from '@helpers/combineUserName';
import { editComment as editCommentAction } from '@redux/actions/mediaViewerAction';
import { openTaskModal } from '@redux/reducers/tasks';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { AssetItemDto, AssetVersionCommentDto, AttachmentDto } from '@api/Api';
import {
  CreateAssetCommentParams,
  UpdateAssetCommentParams
} from '@layouts/types';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';

type TurnToTaskParams = {
  asset: AssetItemDto;
  user: any;
  createTask: any;
  dispatch: any;
  selectedBoardId?: string | null;
  selectedUser?: any;
  isOpenTaskModal?: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
  openTasksInSidebar?: boolean;
};

type BuildCreateCommentPayloadParams = {
  values: SubmitEditorValues;
  selectedVersionId?: string;
  isCheckboxChecked: boolean;
  isAudioVideo: boolean;
  isDocument: boolean;
  playerTime: Array<number | null>;
  activePageNumber: number | null;
  annotations: string | null;
  parentComment?: AssetVersionCommentDto;
};

export function buildEditCommentPayload(
  values: SubmitEditorValues,
  currentComment: AssetVersionCommentDto
) {
  return {
    id: values.idOrParentId,
    text: parseStringWithLinksIntoHtmlLinks(values.text),
    rawText: values.rawText,
    mentions: {
      add: values.mentions.filter(
        (mid) => !currentComment.mentions.some((x) => x.id === mid)
      ),
      remove: currentComment.mentions
        .filter((x) => !values.mentions.includes(x.id))
        .map((i) => i.id)
    },
    groupMentions: {
      add: values.groupMentions.filter(
        (gid) => !currentComment.groupMentions.some((x) => x.id === gid)
      ),
      remove: currentComment.groupMentions
        .filter((x) => !values.groupMentions.includes(x.id))
        .map((i) => i.id)
    },
    documentHighlight: values.documentHighlight,
    documentHighlightRects: values.documentHighlightRects,
    documentHighlightReplacement: values.documentHighlightReplacement
  } as UpdateAssetCommentParams;
}

export function createTurnCommentIntoTask({
  asset,
  user,
  createTask,
  dispatch,
  selectedBoardId,
  selectedUser,
  isOpenTaskModal = true,
  setSelectedCollapse,
  openTasksInSidebar = false
}: TurnToTaskParams) {
  return async (comment: AssetVersionCommentDto) => {
    if (!user || !asset) return;
    try {
      let parsedText = comment.text;
      let parsedName = comment.text
        .replace(/<br[^>]*>/gi, ' ')
        .replace(/<[^>]*>?/gm, '');
      comment.mentions.forEach((el) => {
        parsedName = parsedName.replaceAll(`{{${el.id}}}`, '');
        parsedText = parsedText.replaceAll(
          `{{${el.id}}}`,
          `<span data-type="mention" class="b-suggestion-user" data-label="${combineUserName(
            el
          )}" contenteditable="false">@${combineUserName(el)}</span>`
        );
      });
      comment.groupMentions.forEach((el) => {
        parsedName = parsedName.replaceAll(`{{${el.id}}}`, '');
        parsedText = parsedText.replaceAll(
          `{{${el.id}}}`,
          `<span data-type="mention" class="b-suggestion-user" data-label="${el.name}" contenteditable="false">@${el.name}</span>`
        );
      });
      parsedName = parsedName.replaceAll(
        /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
        ''
      );
      if (parsedName.length > 60) parsedName = `${parsedName.slice(0, 57)}...`;
      parsedText = parsedText.replaceAll(
        /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
        `<span data-type="mention" class="b-suggestion-user" data-label="Deleted User" contenteditable="false">@Deleted User</span>`
      );

      const { task } = await createTask({
        workspaceId: asset.workspaceId,
        campaign: { id: asset.campaignId, name: asset.campaignName },
        name: parsedName.trim() || 'New task',
        description: parseStringWithLinksIntoHtmlLinks(parsedText),
        assetVersionCommentId: comment.id,
        assignees: selectedUser
          ? [{ ...selectedUser, hasAccessToTask: true }]
          : undefined,
        board: { id: selectedBoardId || 'no_assigned' },
        taskPosition: 'start-of-list'
      }).unwrap();

      MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
        addToFavorite: false,
        deleteAsset: false,
        newAsset: false,
        externalLinkShare: false,
        approvalRouting: false,
        newCommentAsset: false,
        annotationLeft: false,
        taskFromComment: true
      });

      editCommentAction({
        ...comment,
        task: { id: task.id }
      });
      if (openTasksInSidebar) {
        setSelectedCollapse?.('tasks');
      }
      if (!isOpenTaskModal) return;
      dispatch(
        openTaskModal({
          taskId: task.id,
          descriptionPlaceholder:
            'Describe your task so your teammates know what to do.'
        })
      );
    } catch (error) {
      console.warn(error, '-> create task');
    }
  };
}

export function buildCreateCommentPayload({
  values,
  selectedVersionId,
  isCheckboxChecked,
  isAudioVideo,
  isDocument,
  playerTime,
  activePageNumber,
  annotations,
  parentComment
}: BuildCreateCommentPayloadParams) {
  const {
    text,
    rawText,
    mentions,
    groupMentions,
    idOrParentId: parentId,
    documentHighlight,
    documentHighlightRects,
    documentHighlightReplacement,
    websitePageUrl
  } = values;
  const timeCode = {
    fromSeconds: playerTime[0] || 0,
    toSeconds: playerTime[1]
  };
  let data: Partial<CreateAssetCommentParams> = {
    text: parseStringWithLinksIntoHtmlLinks(text),
    annotations,
    mentions,
    groupMentions,
    rawText,
    websitePageUrl
  };
  if (isCheckboxChecked && isAudioVideo) data = { ...data, timeCode };
  if (isCheckboxChecked && isDocument) {
    data = {
      ...data,
      documentPage: activePageNumber || undefined,
      documentHighlight,
      documentHighlightRects,
      documentHighlightReplacement
    };
  }

  if (parentId && parentId !== 'new') {
    let replyToRawText = parentComment?.text;
    parentComment?.mentions.forEach((el: any) => {
      replyToRawText = parentComment.text.replace(
        `{{${el.id}}}`,
        `@${combineUserName(el)}`
      );
    });
    parentComment?.groupMentions.forEach((el: any) => {
      replyToRawText = parentComment.text.replace(
        `{{${el.id}}}`,
        `@${el.name}`
      );
    });
    data = { ...data, replyToId: parentId, replyToRawText };
  } else {
    data = { ...data, assetVersionId: selectedVersionId || '' };
  }
  return data as CreateAssetCommentParams;
}

export function mapUppyFilesToAttachments(files: any[]): AttachmentDto[] {
  return files.map((file) => ({
    id: (file.meta.attachmentId || file.id) as string,
    name: file.name || '',
    fileType: file.type as AttachmentDto['fileType'],
    mimeType: file.type,
    size: file.size || 0,
    url: URL.createObjectURL(file.data) || '',
    urlExpiresAt: (file.meta.relativePath || '') as string
  }));
}
